import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

export interface NotificationItem {
  id: string;
  type: string;
  icone: string;
  titre: string;
  message: string;
  dateCreation: Date;
  lu: boolean;
  donnees?: any;
}

const CLE_STOCKAGE_NOTIFICATIONS = 'dematral_notifications_v1';
const MAX_NOTIFICATIONS = 100;

/**
 * Service SSE — reçoit les événements temps réel du backend sans polling ni WebSocket.
 * L'EventSource du navigateur maintient une connexion HTTP persistante.
 * Le token JWT est passé en query param (EventSource ne supporte pas les headers custom).
 */
@Injectable({ providedIn: 'root' })
export class SseNotificationService implements OnDestroy {

  private eventSource: EventSource | null = null;

  /** Après une affectation depuis l’UI, le backend émet aussi AFFECTATION_LIVREUR : on évite le double toast. */
  private toastAffectationIgnoreJusqua = new Map<number, number>();

  /** Émet les données brutes de chaque nouvelle commande reçue via SSE */
  private _nouvelleCommande$ = new Subject<any>();
  readonly nouvelleCommande$ = this._nouvelleCommande$.asObservable();

  /** Émet quand le statut d'une commande existante change (STATUT_COMMANDE, LIVRAISON_CONFIRMEE, AFFECTATION_LIVREUR) */
  private _miseAJourCommande$ = new Subject<{ commandeId: number; statut?: string; livreurNom?: string; livreurId?: number; [key: string]: any }>();
  readonly miseAJourCommande$ = this._miseAJourCommande$.asObservable();

  /** Émet la position GPS du livreur en temps réel (événement POSITION_LIVREUR) */
  private _positionLivreur$ = new Subject<{ commandeId: number; latitude: number; longitude: number; livreurId?: number }>();
  readonly positionLivreur$ = this._positionLivreur$.asObservable();

  /** Liste persistée des notifications reçues (localStorage). Émet à chaque mise à jour. */
  private _notifications$ = new BehaviorSubject<NotificationItem[]>(this.chargerDepuisStockage());
  readonly notifications$ = this._notifications$.asObservable();

  private readonly TYPES: Record<string, { icone: string; couleur: string }> = {
    NOUVELLE_COMMANDE:   { icone: 'fal fa-box-open',    couleur: 'info' },
    STATUT_COMMANDE:     { icone: 'fal fa-sync-alt',    couleur: 'info' },
    AFFECTATION_LIVREUR: { icone: 'fal fa-motorcycle',  couleur: 'success' },
    LIVRAISON_CONFIRMEE: { icone: 'fal fa-check-circle',couleur: 'success' },
    ALERTE_DOCUMENT:     { icone: 'fal fa-exclamation-triangle', couleur: 'warning' },
    POSITION_LIVREUR:    { icone: 'fal fa-map-marker-alt', couleur: '' },
  };

  constructor(
    private auth: AuthService,
    private toast: ToastService
  ) {}

  /** Appeler après une connexion réussie */
  demarrer(): void {
    this.arreter();

    const token = this.auth.obtenirToken();
    if (!token) return;

    const base = environment.apiurl.replace(/\/api\/v1\/?$/, '');
    const url = `${base}/api/v1/notifications/stream?token=${encodeURIComponent(token)}`;

    try {
      this.eventSource = new EventSource(url);

      this.eventSource.onopen = () => {
        console.log('[SSE] Connexion établie');
      };

      // Événements métier
      this.ecouterType('NOUVELLE_COMMANDE');
      this.ecouterType('STATUT_COMMANDE');
      this.ecouterType('AFFECTATION_LIVREUR');
      this.ecouterType('LIVRAISON_CONFIRMEE');
      this.ecouterType('POSITION_LIVREUR');

      this.eventSource.onerror = (err) => {
        console.warn('[SSE] Erreur — reconnexion automatique dans 5s', err);
        // EventSource gère la reconnexion automatiquement
      };
    } catch (e) {
      console.error('[SSE] Impossible de créer EventSource', e);
    }
  }

  /** Appeler à la déconnexion */
  arreter(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      console.log('[SSE] Connexion fermée');
    }
  }

  ngOnDestroy(): void {
    this.arreter();
  }

  /**
   * À appeler juste avant `attribuerLivreurManuellement` : le prochain événement SSE
   * AFFECTATION_LIVREUR pour cette commande n’affichera pas de toast (l’UI affiche déjà le succès).
   */
  ignorerProchainToastAffectationLivreur(commandeId: number, dureeMs = 4000): void {
    if (!commandeId) return;
    this.toastAffectationIgnoreJusqua.set(commandeId, Date.now() + dureeMs);
  }

  // ── Interne ─────────────────────────────────────────────────────────────

  private doitIgnorerToastAffectation(commandeId: number): boolean {
    const fin = this.toastAffectationIgnoreJusqua.get(commandeId);
    if (!fin) return false;
    if (Date.now() > fin) {
      this.toastAffectationIgnoreJusqua.delete(commandeId);
      return false;
    }
    return true;
  }

  /** Marque une notification comme lue */
  marquerCommeLue(id: string): void {
    const liste = this._notifications$.value.map(n => n.id === id ? { ...n, lu: true } : n);
    this._notifications$.next(liste);
    this.sauvegarderDansStockage(liste);
  }

  /** Marque toutes les notifications comme lues */
  marquerToutesCommeLues(): void {
    const liste = this._notifications$.value.map(n => ({ ...n, lu: true }));
    this._notifications$.next(liste);
    this.sauvegarderDansStockage(liste);
  }

  /** Supprime une notification */
  supprimer(id: string): void {
    const liste = this._notifications$.value.filter(n => n.id !== id);
    this._notifications$.next(liste);
    this.sauvegarderDansStockage(liste);
  }

  /** Vide tout l'historique */
  viderTout(): void {
    this._notifications$.next([]);
    this.sauvegarderDansStockage([]);
  }

  private chargerDepuisStockage(): NotificationItem[] {
    try {
      const brut = localStorage.getItem(CLE_STOCKAGE_NOTIFICATIONS);
      if (!brut) return [];
      const liste: NotificationItem[] = JSON.parse(brut);
      return liste.map(n => ({ ...n, dateCreation: new Date(n.dateCreation) }));
    } catch {
      return [];
    }
  }

  private sauvegarderDansStockage(liste: NotificationItem[]): void {
    try {
      localStorage.setItem(CLE_STOCKAGE_NOTIFICATIONS, JSON.stringify(liste.slice(0, MAX_NOTIFICATIONS)));
    } catch { /* quota plein, ignore */ }
  }

  private ajouterNotification(type: string, meta: { icone: string; couleur: string }, data: any): void {
    const item: NotificationItem = {
      id: `${type}-${data.commandeId ?? ''}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      icone: meta.icone || 'fal fa-bell',
      titre: data.titre ?? type,
      message: data.message ?? '',
      dateCreation: new Date(),
      lu: false,
      donnees: data
    };
    const liste = [item, ...this._notifications$.value].slice(0, MAX_NOTIFICATIONS);
    this._notifications$.next(liste);
    this.sauvegarderDansStockage(liste);
  }

  private ecouterType(type: string): void {
    if (!this.eventSource) return;

    this.eventSource.addEventListener(type, (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        const meta = this.TYPES[type] ?? { icone: 'fal fa-bell', couleur: 'info' };
        const titre = data.titre ?? type;
        const message = data.message ?? '';

        // POSITION_LIVREUR n'est pas une vraie notification utilisateur
        if (type !== 'POSITION_LIVREUR') {
          this.ajouterNotification(type, meta, data);
        }

        const ignorerToast =
          type === 'AFFECTATION_LIVREUR' &&
          data.commandeId &&
          this.doitIgnorerToastAffectation(Number(data.commandeId));

        if (!ignorerToast) {
          if (meta.couleur === 'success') {
            this.toast.succes(message, titre);
          } else {
            this.toast.information(message, titre);
          }
        } else {
          this.toastAffectationIgnoreJusqua.delete(Number(data.commandeId));
        }

        // Pour NOUVELLE_COMMANDE : insérer en tête de liste
        if (type === 'NOUVELLE_COMMANDE' && data.commandeId) {
          this._nouvelleCommande$.next({
            id: data.commandeId,
            numeroCommande: data.numeroCommande,
            statut: data.statut ?? 'EN_ATTENTE',
            _nouveau: true
          });
        }

        // Pour les mises à jour de statut : mettre à jour la ligne existante
        if ((type === 'STATUT_COMMANDE' || type === 'LIVRAISON_CONFIRMEE' || type === 'AFFECTATION_LIVREUR') && data.commandeId) {
          this._miseAJourCommande$.next({
            commandeId: data.commandeId,
            statut: data.statut,
            livreurId: data.livreurId,
            livreurNom: data.livreurNom,
            numeroCommande: data.numeroCommande,
          });
        }

        // Pour la position GPS du livreur : pas de toast, juste l'émission de la position
        if (type === 'POSITION_LIVREUR' && data.commandeId && data.latitude != null && data.longitude != null) {
          this._positionLivreur$.next({
            commandeId: Number(data.commandeId),
            latitude: data.latitude,
            longitude: data.longitude,
            livreurId: data.livreurId,
          });
          return; // pas de toast pour les positions
        }
      } catch (e) {
        console.warn('[SSE] Impossible de parser l\'événement', e);
      }
    });
  }
}
